import { usesPerformanceLabNavbar } from 'components/performance-lab/performanceLabNavRoutes';
import { MONSOON_OFFER_ENDS } from 'components/performance-lab/buy/india/planOffer';
import type { BannerCampaign, BannerContext } from './types';
import {
  EXCLUDED_REGIONS,
  isExcludedRegion,
  isRingAirUnavailable,
} from './regions';

/** Master switch. */
export const BANNER_FEATURE_ACTIVE = true;

/** Routes that fall back to the market default even on /buy. */
const DEFAULT_OVERRIDE_ROUTES = new Set([
  '/ring', '/ring/buy',
  '/diesel-ultrahuman-ring', '/diesel-ultrahuman-ring/buy',
  '/rare', '/rare/buy',
]);

const isBuyOrPricing = (p: string) => p.endsWith('/buy') || p.endsWith('/pricing');

/** Ring-AIR-unavailable geos keep the market default on these buy routes. */
const showsDefault = (ctx: BannerContext) =>
  !isBuyOrPricing(ctx.path) ||
  (DEFAULT_OVERRIDE_ROUTES.has(ctx.path) && isRingAirUnavailable(ctx.region));

/** Whole-page suppressions, checked before any campaign. */
export const SUPPRESSED: ((ctx: BannerContext) => boolean)[] = [
  () => !BANNER_FEATURE_ACTIVE,
  ({ path }) => path === '/performance-lab/buy' || usesPerformanceLabNavbar(path),
  // Conditional, matching main: the DEFAULT_OVERRIDE_ROUTES branch returns the
  // market default BEFORE main's `/rare` suppression, so ring-AIR-unavailable
  // geos keep their strip on /rare. Suppressing unconditionally dropped it.
  ({ path, region }) => path === '/rare' && !isRingAirUnavailable(region),
  ({ region }) => !region || region === 'XX',
];

export const BANNER_CAMPAIGNS: BannerCampaign[] = [
  // Pulsomics launch. NO time window: main gates this on a bare
  // `PULSOMICS_CAMPAIGN_ACTIVE = true` with the comment "Flip to false to end
  // the campaign" and ships no date at all, so inventing one here would
  // silently retire a campaign main currently runs. Deleting this row is the
  // equivalent switch. Priority 15 = ahead of the region tiles (20), behind
  // shipping (10), which it can never co-resolve with anyway.
  // `|| isExcludedRegion` matches main: an excluded region on a buy/pricing
  // route outside DEFAULT_OVERRIDE_ROUTES falls through to the market default
  // there, and so still sees this tile.
  {
    id: 'pulsomics',
    priority: 15,
    when: (ctx) => showsDefault(ctx) || isExcludedRegion(ctx.region),
  },
  {
    id: 'shipping',
    priority: 10,
    exceptRegions: EXCLUDED_REGIONS,
    when: (ctx) => !showsDefault(ctx),
  },
  {
    id: 'm2-live',
    priority: 20,
    regions: ['US'],
    when: showsDefault,
  },
  {
    id: 'performance-lab-monsoon',
    priority: 20,
    regions: ['IN'],
    until: MONSOON_OFFER_ENDS,
    when: showsDefault,
  },
  // SDD 2-hour launch takes the IN default strip. main did this by repointing
  // `config.bannerComponent` from PerformanceLabBanner to TwoHourDeliveryBanner
  // and leaving the old component defined-but-unused behind an eslint-disable;
  // here the rotation is data, so `performance-lab-glp` simply keeps its tile
  // in TILES with no campaign entry and can be re-enabled by re-adding one.
  // No end date: main ships none, and inventing one would silently retire this.
  {
    id: 'sdd-two-hour',
    priority: 20,
    regions: ['IN'],
    from: MONSOON_OFFER_ENDS,
    when: showsDefault,
  },
];
